import SectionLabel from "@/components/SectionLabel";
import {
  Database,
  Lock,
  Network,
  Cpu,
  Gauge,
  Scale,
  Shield,
  FileSearch,
} from "lucide-react";

const STEPS = [
  { idx: "01", title: "Ingest", icon: Database, body: "Read what your stack already produces." },
  { idx: "02", title: "Sanitise", icon: Lock, body: "PII protected. GDPR by design." },
  { idx: "03", title: "Entity Resolution", icon: Network, body: "Fragments to canonical entities, with confidence." },
  { idx: "04", title: "Risk Features", icon: Cpu, body: "Composed deterministically across risk components." },
  { idx: "05", title: "Trust Score", icon: Gauge, body: "An independent, comparable risk score — produced the same way, every time." },
  { idx: "06", title: "Gap Analysis", icon: Scale, body: "Where Sentinel agrees with your rating, and where it diverges." },
  { idx: "07", title: "FPRE & FFPRE", icon: Shield, body: "AML and fraud false-positive reduction." },
  { idx: "08", title: "Explain", icon: FileSearch, body: "Top contributors, cited to regulation." },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="light-section py-24 lg:py-32 border-t border-[#e4e4e7]"
    >
      <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <SectionLabel index="05" label="The deterministic pipeline" />
            <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[58px] tracking-tight leading-[1.02]">
              Eight steps.<br />
              <span className="text-[#52525b]">Same input, same output.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#52525b] text-[17px] leading-relaxed">
              No black-box ML in the scoring path. Every assessment is
              reproducible, defensible and cited.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isLast = i === STEPS.length - 1;
            const ACCENTS = [
              { hex: "#0047ff", soft: "rgba(0,71,255,0.10)" },
              { hex: "#00b8c9", soft: "rgba(0,229,255,0.14)" },
              { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
              { hex: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
              { hex: "#10b981", soft: "rgba(16,185,129,0.10)" },
              { hex: "#f43f5e", soft: "rgba(244,63,94,0.10)" },
              { hex: "#0047ff", soft: "rgba(0,71,255,0.10)" },
              { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
            ];
            const a = ACCENTS[i % ACCENTS.length];
            return (
              <div
                key={s.idx}
                data-testid={`how-step-${s.idx}`}
                className="bg-white p-7 lg:p-8 flex flex-col gap-6 group hover:bg-[#fafafa] transition-colors relative min-h-[220px]"
              >
                <span
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ backgroundColor: a.hex }}
                />
                <div className="flex items-center justify-between">
                  <span
                    className="font-mono text-[11px] tracking-[0.22em] uppercase"
                    style={{ color: a.hex }}
                  >
                    {s.idx}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-9 h-9"
                    style={{ backgroundColor: a.soft }}
                  >
                    <Icon
                      className="w-4 h-4"
                      style={{ color: a.hex }}
                      strokeWidth={2}
                    />
                  </span>
                </div>
                <div>
                  <p className="font-display font-bold text-[#090a0f] text-[22px] tracking-tight">
                    {s.title}
                  </p>
                  <p className="mt-2 text-[#52525b] text-[14px] leading-relaxed">
                    {s.body}
                  </p>
                </div>
                {!isLast && (
                  <span className="absolute right-0 top-1/2 hidden lg:block -translate-y-1/2 translate-x-1/2 z-10">
                    <span className="block w-2 h-2 rotate-45 bg-white border-t border-r border-[#e4e4e7]" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
